import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Attachment,
  Documentation,
  useEdmDocumentationQuery,
} from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import TopNavigationHeader from '@lib/layouts/AppLayout/TopNavigationHeader/TopNavigationHeader';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import DocumentDetails from './components/DocumentDetails';
import styles from './DocumentView.module.scss';

export default function DocumentViewContainer() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const { getUserAccessByCompany }: ContextProps = useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const { data: queryData, loading } = useEdmDocumentationQuery({
    variables: { id, isViewLocked },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const data = queryData?.data;

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const isFavoriteLoading = favoriteLoading || unfavoriteLoading;

  const onToggleFavorite = useCallback(
    (item: Documentation) => {
      const isFavorited = item.favorited;
      const mutation = isFavorited ? bulkUnfavorite : bulkFavorite;
      const successMessage = isFavorited
        ? t('remove-from-favorites-success')
        : t('add-to-favorites-success');
      mutation({
        variables: { ids: [item.id] },
        update: (cache) => {
          cache.modify({
            id: cache.identify(item),
            fields: {
              favorited: () => !isFavorited,
            },
          });
        },
        onCompleted: () => {
          toast({ content: successMessage });
        },
      });
    },
    [bulkFavorite, bulkUnfavorite, t],
  );

  const { name, currentVersion } = data || {};

  return (
    <>
      <TopNavigationHeader
        title={t('document-details')}
        backUrl={APP_URLS.app.documentation.index.getPathWithQuery()}
        predictorFnUsePrevUrl={() => true}
      />
      <PageBody withoutHeader>
        {loading && !data ? (
          <CircleLoader />
        ) : (
          data && (
            <AppContentMaxWidth>
              <div className={styles.content}>
                <DashboardTitleHeader
                  title={name}
                  bottomSpace="none"
                  actions={
                    isViewLocked
                      ? []
                      : [
                          <IconButton
                            key="favorite"
                            icon={
                              data?.favorited ? 'bookmark_star' : 'bookmark'
                            }
                            onClick={() =>
                              onToggleFavorite(data as Documentation)
                            }
                            symbolsOutlined
                            filled={data?.favorited ?? false}
                            isLoading={isFavoriteLoading}
                            type={IconButtonTypes.PrimaryOutlined}
                          />,
                        ]
                  }
                />
                <DocumentDetails
                  data={data as Documentation}
                  isViewLocked={isViewLocked}
                  onViewFile={() => setIsFileViewerOpen(true)}
                />
              </div>
            </AppContentMaxWidth>
          )
        )}
      </PageBody>
      <FileViewerModal
        attachment={currentVersion?.attachmentAttached as Attachment}
        documentationVersionId={currentVersion?.id}
        isOpen={isFileViewerOpen}
        onClose={() => setIsFileViewerOpen(false)}
        useDownloadTool={false}
        useFavoriteTool={!isViewLocked}
        onFavoriteToggle={
          !isViewLocked && data
            ? () => onToggleFavorite(data as Documentation)
            : undefined
        }
        isFavorited={data?.favorited ?? false}
      />
    </>
  );
}
